import React, { useState } from 'react';
import { effectiveRouterVersion } from '@/utils/routerVersion';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { colors } from '@/constants/colors';
import { isHybridMigrationPaused } from '@/constants/hybridMigration';
import { coinBoxFirstText } from '@/utils/firstHeartbeat';
import MikrotikHybridSwitch from './MikrotikHybridSwitch';
import {
   getDeviceUpdateStatus,
   updateDeviceFirmware,
   cancelDeviceFirmwareUpdate,
   setAutoUpdateAll,
   clearAutoUpdateAll,
   setNodeMcuOta,
   applyRouterConfig,
   migrateDeviceVariant,
   type DeviceUpdateStatus,
   type DeviceUpdateState,
} from '@/api/vendo';
import {
   nextQuietHour,
   quietHourAfter,
   toLocalInputValue,
   scheduledLine,
   whenLabel,
   quietHourLabel,
} from '@/utils/updateSchedule';

/**
 * Operator self-service Updates tab (2d). One row per updatable component
 * — router config (hEX), router firmware (Ruijie), NodeMCU firmware —
 * each showing running-vs-latest with a single "Update" action. The
 * admin-approved registry is the only version source: the operator can
 * only move a component to the current version, never pick one.
 */

interface Props {
   deviceId: string;
   /** Router liveness, from the device the parent already holds. An update is a
    *  PULL — an offline box cannot collect one — so the buttons are greyed
    *  rather than letting the operator click and get a 409 back. */
   routerStatus?: 'connected' | 'agent-down' | 'disconnected' | 'unprovisioned';
   /** Which product this vendo is registered as — drives the migrate row. */
   variant?: 'cloud' | 'hybrid';
   platform?: string;
   isSubVendo?: boolean;
   /** Platform admins only. The SERVER enforces this; hiding the control just
    *  avoids offering a button that would come back 403. */
   canRevert?: boolean;
}

/** How many components are behind — drives the tab's "outdated" pill. */
export function outdatedCount(s?: DeviceUpdateStatus | null): number {
   if (!s) return 0;
   let n = 0;
   if (s.routerConfig?.stale) n++;
   // A waiting SCRIPT update counts, and `firmware.stale` never sees one — it
   // only tracks the reflash. Without this the row showed an Update button
   // under a header reading "Everything is up to date", and the "N behind"
   // pill on the tab said nothing was behind. Ray caught it on staging with
   // 2.3.0 sitting right there (2026-08-25).
   //
   // Counted as ONE either way: the button installs one update, whichever kind
   // it turns out to be, so two would overstate what is waiting.
   if (pendingUpdateKind(s.firmware ?? null)) n++;
   if (s.coinBox?.stale) n++;
   return n;
}

export interface ReleaseRow {
   version: string;
   notes: string;
   at?: string | null;
   running?: boolean;
   offered?: boolean;
}

/**
 * Collapsible "What's new" — the note for the version being offered, plus a
 * "View All" that opens every released version for this vendo's own board and
 * product.
 *
 * Ray asked for the full list because one note answers "what am I about to
 * install" but not "what have I been missing" — a router four versions behind
 * gets one note describing the newest change and nothing about the three in
 * between.
 *
 * The list carries a version and its upload note only. It is NOT the admin
 * firmware list, which is gated behind requireTab('firmware') because it also
 * carries dev-channel builds, every board, storage keys and uploader ids.
 */
export const WhatsNew: React.FC<{ notes: string; history?: ReleaseRow[] }> = ({
   notes,
   history,
}) => {
   const [open, setOpen] = useState(false);
   const [all, setAll] = useState(false);

   // Only rows that actually say something. A history of empty notes is a list
   // of version numbers, which is not what was asked for.
   const rows = (history ?? []).filter((r) => r.version);
   // "View All" earns its place only when there is more than the note already
   // shown — otherwise it opens onto a single row the operator is looking at.
   const worthListing = rows.length > 1;

   if (!notes.trim() && !worthListing) return null;

   return (
      <NotesWrap>
         {/* 🔴 TWO SIBLINGS, not one nested in the other.
             "View all" was first built INSIDE the What's new panel, so it only
             appeared after expanding that — and Ray, looking at the shipped
             screen: "wheres the view all update?". He had asked for it BELOW
             What's new. A control you must open another control to discover is
             not discoverable. */}
         <ToggleRow>
            <NotesToggle
               type="button"
               aria-expanded={open}
               onClick={() => setOpen((v) => !v)}
            >
               <Caret $open={open}>▸</Caret> What’s new
            </NotesToggle>
            {worthListing && (
               <ViewAllBtn
                  type="button"
                  aria-expanded={all}
                  onClick={() => setAll((v) => !v)}
               >
                  {all ? 'Hide older versions' : `View all (${rows.length})`}
               </ViewAllBtn>
            )}
         </ToggleRow>
         {open && (
            notes.trim() ? (
               <NotesBody>{notes}</NotesBody>
            ) : (
               /* Say so rather than opening onto nothing — an expander that
                  reveals empty space reads as broken. */
               <NotesBody $muted>No note was written for this version.</NotesBody>
            )
         )}
         {(() => (
            <>
               {all && (
                  <HistoryList>
                     {rows.map((r) => (
                        <HistoryRow key={r.version}>
                           <HistoryHead>
                              <HistoryVer>{r.version}</HistoryVer>
                              {/* Says where the operator IS, so a long list does
                                  not leave them counting rows to find it. */}
                              {r.running && <HereTag>on this vendo now</HereTag>}
                              {r.offered && !r.running && (
                                 <OfferTag>available</OfferTag>
                              )}
                              {r.at && <HistoryDate>{fmtDate(r.at)}</HistoryDate>}
                           </HistoryHead>
                           {r.notes.trim() ? (
                              <HistoryNotes>{r.notes}</HistoryNotes>
                           ) : (
                              <HistoryNotes $muted>No note was written for this one.</HistoryNotes>
                           )}
                        </HistoryRow>
                     ))}
                  </HistoryList>
               )}
            </>
         ))()}
      </NotesWrap>
   );
};

/** "31 Aug 2026" — a date an operator can match against their own memory. */
function fmtDate(iso: string): string {
   const d = new Date(iso);
   if (!Number.isFinite(d.getTime())) return '';
   return d.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
   });
}

// Is the offered version OLDER than what's running? (base compare, ignoring the
// -<sha> suffix). We deliberately allow "downgrades" — re-activating a previous
// version is the intended ROLLBACK path — but we label them as a rollback, not
// "new firmware available", so the operator isn't confused (e.g. running 1.0.4,
// active 1.0.2).
const fwBaseParts = (v?: string | null) =>
   (v ?? '').split('-')[0].split('.').map((n) => parseInt(n, 10));
function offeredIsOlder(target?: string | null, running?: string | null): boolean {
   const a = fwBaseParts(target);
   const b = fwBaseParts(running);
   if (!a.length || !b.length || a.some(Number.isNaN) || b.some(Number.isNaN))
      return false;
   for (let i = 0; i < Math.max(a.length, b.length); i++) {
      const x = a[i] ?? 0;
      const y = b[i] ?? 0;
      if (x !== y) return x < y;
   }
   return false;
}

const UpdatesTab: React.FC<Props> = ({
   deviceId,
   routerStatus,
   variant,
   platform,
   isSubVendo,
   canRevert,
}) => {
   const queryClient = useQueryClient();
   const [busy, setBusy] = useState<string | null>(null);
   // Two-step, because this changes which PRODUCT the vendo is and reboots the
   // router. The confirm is where the NodeMCU note gets read — operators would
   // otherwise update the NodeMCU first "to be safe" and cause a second reboot
   // for nothing.
   const [confirming, setConfirming] = useState<'to-hybrid' | 'to-cloud' | null>(null);
   // Scheduled firmware update (2026-09-09): "Update later…" opens the picker;
   // the value is the operator's LOCAL time as the <input type=datetime-local>
   // gives it. Converted to UTC only when sent.
   const [scheduling, setScheduling] = useState(false);
   const [scheduleAt, setScheduleAt] = useState('');
   const [scheduleErr, setScheduleErr] = useState<string | null>(null);
   // "Do this for all my vendos, every time there is a new update" — a standing
   // rule saved on every router the operator owns, so they never have to open
   // each device again (Ray, 2026-09-10).
   const [autoAll, setAutoAll] = useState(false);

   const { data: status, isLoading } = useQuery({
      queryKey: ['device-update-status', deviceId],
      queryFn: () => getDeviceUpdateStatus(deviceId),
      // Poll faster while an update is mid-flight so the pill + rows track
      // the box coming back; idle otherwise.
      refetchInterval: (q) =>
         (q.state.data as DeviceUpdateStatus | undefined)?.updateState &&
         (q.state.data as DeviceUpdateStatus).updateState !== 'idle'
            ? 5000
            : 20000,
   });

   const refresh = () =>
      queryClient.invalidateQueries({
         queryKey: ['device-update-status', deviceId],
      });

   const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
      setBusy(key);
      try {
         await fn();
         toast.success(ok);
         await refresh();
      } catch (e: any) {
         const data = e?.response?.data;
         // "This vendo is already getting X" is NOT a failure — the update the
         // operator wanted is already running. It was showing as a red error
         // toast, which reads as "something broke" at the exact moment
         // everything is working (Ray, 2026-08-19).
         //
         // It only appears when a per-device target was armed by something
         // other than this button — a canary "Send to…", or the migrate button.
         // Pressing Update normally arms it and stamps updatingAt, so the row
         // switches to "Updating…" and the button disables itself.
         if (data?.code === 'update-in-flight') {
            toast.info(data.error);
            await refresh();
            return;
         }
         toast.error(data?.error || 'Update failed — try again');
      } finally {
         setBusy(null);
      }
   };

   if (isLoading || !status) {
      return <Muted>Checking for updates…</Muted>;
   }

   const anyStale = outdatedCount(status) > 0;
   // An update is a PULL: the box fetches it on its next heartbeat. Offline, it
   // never will — and an armed target it cannot collect is what left devices
   // reading "updating" forever. Say so on the button instead of failing after.
   const blockedReason =
      routerStatus === 'connected' || routerStatus === undefined
         ? null
         : routerStatus === 'agent-down'
           ? 'The router is reachable but SmartFi is not running on it — restart the box first.'
           : 'The router is offline. Turn it on and try again once it shows online.';
   const busyOrUpdating = (key: string, state: DeviceUpdateState) =>
      busy === key || state !== 'idle';

   // Switching product is BLOCKED while any update is in flight.
   //
   // Ray found this on staging 2026-08-19: "Move back to Cloud" stayed clickable
   // while the Ruijie router was mid-update. Pressing it there would flip the
   // record and arm the OTHER version while the router is already downloading
   // one — so it would land on the version it was fetching, then immediately be
   // told to flash back. Two flashes and two reboots from one stray click, on a
   // vendo that is already offline to its customers.
   //
   // The firmware rows have always guarded this with busyOrUpdating; the switch
   // needs the same rule, plus the router's own state because a migration arms
   // the router without the operator touching the Router firmware row.
   // ⚠️ Each row is checked only when it EXISTS. A Ruijie has no router-config
   // row at all, so `status.routerConfig?.state !== 'idle'` reads undefined !==
   // 'idle' and is TRUE — which disabled the switch on every Ruijie. Caught by
   // the confirm tests going red, not by reading the code.
   const rowBusy = (row?: { state?: DeviceUpdateState } | null) =>
      !!row && !!row.state && row.state !== 'idle';

   const updateInFlight =
      (!!status.updateState && status.updateState !== 'idle') ||
      rowBusy(status.firmware) ||
      rowBusy(status.coinBox) ||
      rowBusy(status.routerConfig);

   // A scheduled firmware update that has not fired yet. Once it fires the row
   // state leaves 'idle' and the ordinary "Updating…" takes over.
   const firmwareScheduledAt =
      status.firmware?.state === 'idle' ? status.firmware.scheduledAt ?? null : null;

   const switchBlockedReason = blockedReason
      ? blockedReason
      : updateInFlight
      ? 'An update is running on this vendo. Wait for it to finish, then switch.'
      : firmwareScheduledAt
      ? 'A firmware update is scheduled for this vendo. Cancel it first, then switch.'
      : null;

   const openScheduler = () => {
      setScheduleAt(toLocalInputValue(nextQuietHour(new Date())));
      setScheduleErr(null);
      setScheduling(true);
   };
   const submitSchedule = async () => {
      const at = new Date(scheduleAt);
      if (!scheduleAt || Number.isNaN(at.getTime())) {
         setScheduleErr('Pick a date and time.');
         return;
      }
      if (at.getTime() <= Date.now()) {
         setScheduleErr('That time has already passed. Pick a later one.');
         return;
      }
      if (at.getTime() - Date.now() > 14 * 86400000) {
         setScheduleErr('Pick a time within the next 14 days.');
         return;
      }
      setScheduleErr(null);
      const rule = { hour: at.getHours(), minute: at.getMinutes(), tzOffsetMin: at.getTimezoneOffset() };
      await run(
         'firmware',
         async () => {
            await updateDeviceFirmware(deviceId, at.toISOString());
            if (autoAll) await setAutoUpdateAll(rule);
         },
         autoAll
            ? `Update scheduled for ${whenLabel(at)} — and from now on every new update installs at ${quietHourLabel(rule)} on all your vendos`
            : `Update scheduled for ${whenLabel(at)} — the router restarts then`
      );
      setScheduling(false);
   };
   const turnOffAuto = () =>
      run(
         'auto-off',
         () => clearAutoUpdateAll(),
         'Automatic updates turned off on all your vendos'
      );

   // Cloud -> Hybrid is paused fleet-wide; the why lives with the flag.
   // Nobody gets the forward switch while it is on, admins included. The
   // rescue direction below is gated on canRevert, not on this, so moving a
   // broken vendo BACK to Cloud stays available.
   const canGoHybrid = !isHybridMigrationPaused();
   const migratePausedNote =
      variant !== 'hybrid' && !canGoHybrid
         ? 'Switching to Hybrid is paused while we ship a fix. Your vendo keeps running on Cloud — nothing to do.'
         : null;

   return (
      <Wrap>
      {/* ── Switch product: cloud ⇄ hybrid ──────────────────────────────────
          FIRST on the tab, above the firmware rows — Ray, 2026-08-19: "this
          switch is in very top because this is important to see".
          This box is the Ruijie / OrangePi one, MAIN vendo only: a sub-vendo
          shares its main vendo's router.
          Hidden outright when there is nothing to press — a hybrid vendo shown
          to a non-admin has no action, and an empty box is noise. */}
      {/* A MikroTik has its own switch: its move is not finished when the
          button returns, so it follows the steps instead (see the file). */}
      {platform === 'mikrotik' && !isSubVendo && (
         <MikrotikHybridSwitch deviceId={deviceId} routerStatus={routerStatus} />
      )}
      {platform === 'openwrt' && !isSubVendo && (variant !== 'hybrid' || canRevert) && (
         <MigrateBox>
            {/* While the switch is paused the "Cloud → Hybrid" line is a promise
                we are not keeping this minute, so the note replaces it. */}
            {!migratePausedNote && (
               <SwitchLine>
                  You’re about to switch{' '}
                  <Was>{variant === 'hybrid' ? 'Hybrid' : 'Cloud'}</Was>
                  {' → '}
                  <Will>{variant === 'hybrid' ? 'Cloud' : 'Hybrid'}</Will>
               </SwitchLine>
            )}

            {confirming ? (
               <ConfirmBox>
                  <strong>
                     {confirming === 'to-hybrid'
                        ? 'Switch this vendo to Hybrid?'
                        : 'Move this vendo back to Cloud?'}
                  </strong>
                  <ul>
                     <li>The router restarts — customers drop offline for a minute or two.</li>
                     {/* The one nobody expects. Proven on hardware 2026-08-19:
                         a cloud NodeMCU keeps taking coins under a hybrid
                         router, so there is nothing to do to it first. */}
                     <li>
                        <strong>The NodeMCU is not changed.</strong> It keeps taking
                        coins throughout, and can be updated separately afterwards.
                     </li>
                     <li>Customers keep their remaining time.</li>
                  </ul>
                  <ConfirmRow>
                     <GhostBtn
                        type="button"
                        onClick={() => setConfirming(null)}
                        disabled={!!busy}
                     >
                        Cancel
                     </GhostBtn>
                     <PrimaryBtn
                        type="button"
                        $danger={confirming === 'to-cloud'}
                        disabled={!!busy}
                        onClick={() => {
                           const dir = confirming;
                           setConfirming(null);
                           run(
                              'migrate',
                              () => migrateDeviceVariant(deviceId, dir),
                              dir === 'to-hybrid'
                                 ? 'Switching to Hybrid — the router updates on its own in a few minutes'
                                 : 'Moving back to Cloud — the router updates on its own in a few minutes'
                           );
                        }}
                     >
                        {busy === 'migrate'
                           ? 'Working…'
                           : confirming === 'to-hybrid'
                           ? 'Yes, switch to Hybrid'
                           : 'Yes, back to Cloud'}
                     </PrimaryBtn>
                  </ConfirmRow>
               </ConfirmBox>
            ) : migratePausedNote ? null : (
               <ConfirmRow>
                  {variant !== 'hybrid' && canGoHybrid && (
                     <PrimaryBtn
                        type="button"
                        disabled={!!busy || !!switchBlockedReason}
                        title={switchBlockedReason || undefined}
                        onClick={() => setConfirming('to-hybrid')}
                     >
                        Switch to Hybrid
                     </PrimaryBtn>
                  )}
                  {/* Admins only. The server refuses it for anyone else — this
                      only avoids showing a button that would come back 403. */}
                  {variant === 'hybrid' && canRevert && (
                     <GhostBtn
                        type="button"
                        disabled={!!busy || !!switchBlockedReason}
                        title={switchBlockedReason || undefined}
                        onClick={() => setConfirming('to-cloud')}
                     >
                        Move back to Cloud
                     </GhostBtn>
                  )}
               </ConfirmRow>
            )}
            {migratePausedNote && <Note>{migratePausedNote}</Note>}
            {switchBlockedReason && !migratePausedNote && (
               <Note>{switchBlockedReason}</Note>
            )}
         </MigrateBox>
      )}
         {/* Who touched this vendo last. It is here because a SELLER can
             update a vendo they sold — so the owner gets the answer to "why
             did my vendo restart" on the screen instead of in a chat. */}
         {status.lastServicedBy?.at && (
            <ServicedBy>
               Last updated by <strong>{status.lastServicedBy.email}</strong>
               {status.lastServicedBy.bySeller && ' (who sold this vendo)'}
               {' · '}
               {new Date(status.lastServicedBy.at).toLocaleString()}
            </ServicedBy>
         )}

         {!anyStale && status.updateState === 'idle' && (
            <Ok>✓ Everything is up to date.</Ok>
         )}

         {/* Router config — MikroTik push only */}
         {status.routerConfig && (
            <Row>
               <RowMain>
                  <RowTitle>
                     Router settings
                     <VersionTransition
                        from={`v${status.routerConfig.appliedVersion}`}
                        to={`v${status.routerConfig.currentVersion}`}
                        stale={status.routerConfig.stale}
                     />
                  </RowTitle>
                  <RowMeta>
                     {status.routerConfig.needsRepair
                        ? // Right version, but the setup never fully landed on the
                          // router. Say what it COSTS, not what it is — an operator
                          // won't act on "config incomplete", but they will act on
                          // "customers can't get online".
                          `Setup did not finish on this router — customers may not get internet after paying. Press Repair.`
                        : status.routerConfig.stale
                          ? `A newer configuration is available. Your router is on v${status.routerConfig.appliedVersion}.`
                          : `Up to date (v${status.routerConfig.appliedVersion}).`}
                     {status.routerConfig.lastAppliedError && (
                        <ErrLine>
                           Last attempt failed: {status.routerConfig.lastAppliedError}
                        </ErrLine>
                     )}
                  </RowMeta>
               </RowMain>
               <RowAction
                  stale={status.routerConfig.stale}
                  needsRepair={status.routerConfig.needsRepair}
                  state={status.routerConfig.state}
                  busy={busy === 'config'}
                  onUpdate={() =>
                     run(
                        'config',
                        () => applyRouterConfig(deviceId),
                        'Router update started — it finishes on its own in a few minutes (longer on a big router). This row shows the new version when done.'
                     )
                  }
                  disabled={busyOrUpdating('config', status.routerConfig.state)}
                  blockedReason={blockedReason}
               />
               <RowBanner state={status.routerConfig.state} what="router" />
            </Row>
         )}

         {/* Router firmware — Ruijie / Lite */}
         {status.firmware && (() => {
            const pendingKind = pendingUpdateKind(status.firmware);
            return (
            <Row>
               <RowMain>
                  <RowTitle>
                     Router firmware
                     <VersionTransition
                        from={effectiveRouterVersion(status.firmware.running, status.firmware.runningBundle).shown}
                        to={
                           pendingKind === 'scripts'
                              ? status.firmware.scriptUpdate!.version
                              : status.firmware.latest
                        }
                        stale={!!pendingKind}
                     />
                  </RowTitle>
                  <RowMeta>
                     {pendingKind === 'scripts'
                        ? `Update available: ${status.firmware.scriptUpdate!.version}. Running ${status.firmware.running || 'unknown'}${
                             status.firmware.runningBundle
                                ? ` + no-reboot update ${status.firmware.runningBundle}`
                                : ''
                          }.`
                        : pendingKind === 'firmware'
                          ? offeredIsOlder(
                               status.firmware.latest,
                               status.firmware.running
                            )
                             ? `Rollback available — the active version (${status.firmware.latest}) is OLDER than what's running (${status.firmware.running}). Applying it downgrades this device.`
                             : `New firmware available: ${status.firmware.latest}. Running ${status.firmware.running || 'unknown'}.`
                          : `Up to date (${status.firmware.running || 'unknown'}${
                             status.firmware.runningBundle
                                ? ` + no-reboot update ${status.firmware.runningBundle}`
                                : ''
                          }).`}
                     {status.firmware.otaStatus?.error && (
                        <ErrLine>
                           Last attempt failed: {status.firmware.otaStatus.error}
                        </ErrLine>
                     )}
                     {/* Only when a reflash is actually on offer. It is true
                         of a firmware update and of nothing else: a script
                         update swaps a few files and restarts the agent, so
                         nobody drops, and an up-to-date router is not about to
                         do either.
                         Left showing everywhere it would train operators to
                         postpone the safe update for the same reason they
                         postpone the risky one — exactly backwards. */}
                     {pendingKind === 'firmware' && (
                        <Note>
                           Updating firmware restarts the router — customers drop
                           offline for a minute or two.
                        </Note>
                     )}
                  </RowMeta>
               </RowMain>
               {firmwareScheduledAt ? (
                  /* Scheduled and waiting: NOT "Updating…" — nothing is
                     happening on the router yet, and a spinner for nine hours
                     would read as stuck. Say when, and offer the way out. */
                  <ScheduledWrap>
                     <ScheduledTag>
                        ⏰ {scheduledLine(firmwareScheduledAt, new Date())}
                     </ScheduledTag>
                     <GhostBtn
                        disabled={busy === 'firmware-cancel'}
                        onClick={() =>
                           run(
                              'firmware-cancel',
                              () => cancelDeviceFirmwareUpdate(deviceId),
                              'Scheduled update cancelled'
                           )
                        }
                     >
                        {busy === 'firmware-cancel' ? 'Cancelling…' : 'Cancel'}
                     </GhostBtn>
                  </ScheduledWrap>
               ) : (
                  <ActionStack>
                     <RowAction
                        stale={!!pendingKind}
                        state={status.firmware.state}
                        busy={busy === 'firmware'}
                        onUpdate={() =>
                           run(
                              'firmware',
                              () => updateDeviceFirmware(deviceId),
                              pendingKind === 'scripts'
                                 ? 'Update started — this one is safe and nobody drops offline'
                                 : 'Firmware update started — the router will restart shortly'
                           )
                        }
                        disabled={busyOrUpdating('firmware', status.firmware.state)}
                        blockedReason={blockedReason}
                     />
                     {/* Only for a REFLASH: a script update drops nobody, so
                         there is nothing to wait for the night for. */}
                     {pendingKind === 'firmware' &&
                        status.firmware.state === 'idle' &&
                        !scheduling && (
                           <LaterBtn
                              disabled={busy === 'firmware' || !!blockedReason}
                              title={blockedReason ?? undefined}
                              onClick={openScheduler}
                           >
                              📅 I want to schedule this update
                           </LaterBtn>
                        )}
                  </ActionStack>
               )}
               <RowBanner state={status.firmware.state} what="router" />
               {/* The standing rule, shown on EVERY router that carries it —
                   including an up-to-date one — so it can be turned off from
                   any device, not only the one it was set from. */}
               {status.firmware.autoUpdate && (
                  <AutoLine>
                     🔁 Automatic: new updates install at{' '}
                     <strong>{quietHourLabel(status.firmware.autoUpdate)}</strong> on all
                     your vendos.
                     <AutoOffBtn
                        type="button"
                        disabled={busy === 'auto-off'}
                        onClick={turnOffAuto}
                     >
                        {busy === 'auto-off' ? 'Turning off…' : 'Turn off'}
                     </AutoOffBtn>
                  </AutoLine>
               )}
               {scheduling && !firmwareScheduledAt && (
                  <ScheduleBox>
                     <strong>Update while the shop is closed.</strong> The router
                     restarts for a minute or two at the time you pick. Times are in
                     your own clock.
                     <PickGrid>
                        {[
                           { label: 'Tonight 2:00 AM', at: nextQuietHour(new Date()) },
                           { label: 'Tomorrow night 2:00 AM', at: quietHourAfter(new Date()) },
                        ].map((q) => {
                           const v = toLocalInputValue(q.at);
                           return (
                              <QuickPick
                                 key={q.label}
                                 type="button"
                                 $on={scheduleAt === v}
                                 onClick={() => {
                                    setScheduleAt(v);
                                    setScheduleErr(null);
                                 }}
                              >
                                 {q.label}
                                 <small>{whenLabel(q.at)}</small>
                              </QuickPick>
                           );
                        })}
                        <PickCard>
                           <label htmlFor={`sched-${deviceId}`}>Or pick a time</label>
                           <input
                              id={`sched-${deviceId}`}
                              type="datetime-local"
                              value={scheduleAt}
                              min={toLocalInputValue(new Date())}
                              onChange={(e) => {
                                 setScheduleAt(e.target.value);
                                 setScheduleErr(null);
                              }}
                           />
                        </PickCard>
                     </PickGrid>
                     {scheduleErr && <ErrLine>{scheduleErr}</ErrLine>}
                     <FootRow>
                        {!status.firmware.autoUpdate ? (
                           <AutoAllRow>
                              <input
                                 id={`auto-all-${deviceId}`}
                                 type="checkbox"
                                 checked={autoAll}
                                 onChange={(e) => setAutoAll(e.target.checked)}
                              />
                              <label htmlFor={`auto-all-${deviceId}`}>
                                 Do this for all my vendos, every time there is a new update
                                 {scheduleAt && !Number.isNaN(new Date(scheduleAt).getTime())
                                    ? ` (every day at ${quietHourLabel({
                                         hour: new Date(scheduleAt).getHours(),
                                         minute: new Date(scheduleAt).getMinutes(),
                                      })})`
                                    : ''}
                              </label>
                           </AutoAllRow>
                        ) : (
                           <span />
                        )}
                        <FootBtns>
                           <GhostBtn
                              disabled={busy === 'firmware'}
                              onClick={() => setScheduling(false)}
                           >
                              Not now
                           </GhostBtn>
                           <PrimaryBtn
                              disabled={busy === 'firmware'}
                              onClick={submitSchedule}
                           >
                              {busy === 'firmware' ? 'Scheduling…' : 'Schedule update'}
                           </PrimaryBtn>
                        </FootBtns>
                     </FootRow>
                  </ScheduleBox>
               )}
               {/* Out here, not in RowMain: only a DIRECT child of Row can take
                   its own full-width line. Nested inside the left column it was
                   squeezed against the Update button. */}
               <WhatsNew notes={status.firmware.notes} history={status.firmware.history} />

               {/* ⚠️ ONLY WHEN THERE IS SOMETHING TO PRESS. The first version
                   showed the warning on an up-to-date router, which is a
                   warning about nothing — and a warning that appears when it
                   does not apply is one people learn to scroll past.
                   0.87% is the measured figure: 1 casualty in 115 on a real
                   rollout. Three or four Ruijies have since been reported dead,
                   so treat it as a floor rather than a ceiling. */}
               {pendingKind === 'firmware' && (
                  <RiskBanner $safe={false}>
                     <RiskTitle>Firmware update — kernel, WiFi radio, OpenWRT</RiskTitle>
                     0.87% chance of bricking if the power cuts off mid-update.
                     Update when the shop has stable power.
                  </RiskBanner>
               )}
               {pendingKind === 'scripts' && (
                  <RiskBanner $safe>
                     <RiskTitle>Script update</RiskTitle>
                     100% safe — a power cut mid-update cannot damage this router.
                  </RiskBanner>
               )}
            </Row>
            );
         })()}

         {/* NodeMCU firmware — NodeMCU */}
         {status.coinBox && (
            <Row>
               <RowMain>
                  <RowTitle>
                     NodeMCU firmware
                     <VersionTransition
                        from={status.coinBox.running}
                        // Suppress the chip only when nothing was ever
                        // flashed — with no running version it falls back to
                        // the LATEST one, which reads as though the box is
                        // already running firmware it has never had.
                        //
                        // A flashed-but-never-powered board is different: the
                        // flasher writes the ACTIVE release at registration,
                        // so `latest` IS the firmware this board carries — say
                        // so (Ray, 2026-09-10).
                        to={
                           status.coinBox.neverPowered
                              ? status.coinBox.latest
                              : status.coinBox.neverFlashed
                                ? null
                                : status.coinBox.latest
                        }
                        stale={status.coinBox.stale}
                     />
                  </RowTitle>
                  <RowMeta>
                     {/* Three states, not two. A box whose flash was skipped
                         during registration has never told us a version, so it
                         is neither behind nor current — saying either one is a
                         guess, and both readings confused operators on a vendo
                         they had just finished setting up. */}
                     {status.coinBox.neverPowered
                        ? /* 🔴 Flashed during registration, never plugged in.
                             This used to read as "not flashed yet" and, worse,
                             as "too old to update over WiFi — needs a one-time
                             USB re-flash", sending the operator for a laptop
                             and a cable over a board with no power. */
                          coinBoxFirstText('needs-power')
                        : status.coinBox.neverFlashed
                        ? coinBoxFirstText('never-flashed')
                        : status.coinBox.stale
                        ? `New firmware available. Running ${status.coinBox.running || 'unknown'}.`
                        : `Up to date (${status.coinBox.running || 'unknown'}).`}
                     {/* Never on a box we have never heard from. `stale` is
                         already false in that case server-side, so this guard
                         is belt-and-braces — but both sentences below send an
                         operator for a laptop and a cable, so they do not get
                         to depend on a field staying false somewhere else. */}
                     {!status.coinBox.neverPowered &&
                        !status.coinBox.neverFlashed &&
                        status.coinBox.stale &&
                        !status.coinBox.canOta && (
                           <Note>
                              {status.coinBox.usbReason === 'wired-too-old' ? (
                                 <>
                                    This NodeMCU is connected by network cable,
                                    and its firmware is too old to update that
                                    way. Flash it once from a computer to{' '}
                                    <strong>1.0.20</strong> and it will update by
                                    itself from then on.
                                 </>
                              ) : (
                                 <>
                                    This NodeMCU is too old to update over WiFi
                                    — it needs a one-time USB re-flash from a
                                    computer.
                                 </>
                              )}
                           </Note>
                        )}
                     {/* GAVE UP. The server stops offering after a few failed
                         downloads, but the target stays set — so this row said
                         "Updating…" forever for something that would never
                         happen. An operator has to be able to tell "still
                         working" from "stopped, and waiting for you". */}
                     {status.coinBox.waitingForBox && (
                        <Note>
                           Waiting for the NodeMCU — it has not checked in
                           recently, so {status.coinBox.sentVersion} cannot be
                           delivered yet. It will install by itself once the box
                           is reachable again.
                        </Note>
                     )}
                     {status.coinBox.gaveUp && (
                        <Note>
                           Tried {status.coinBox.attempts ?? 0} times to install{' '}
                           {status.coinBox.sentVersion} and could not finish the
                           download, so it stopped. The NodeMCU is still working
                           on {status.coinBox.running}. Send it again to retry.
                        </Note>
                     )}
                  </RowMeta>
               </RowMain>
               {status.coinBox.neverPowered ||
               status.coinBox.neverFlashed ? null : status.coinBox.stale &&
                 !status.coinBox.canOta ? (
                  <ManualTag>USB re-flash</ManualTag>
               ) : (
                  <RowAction
                     stale={status.coinBox.stale}
                     state={status.coinBox.state}
                     busy={busy === 'coinbox'}
                     onUpdate={() =>
                        run(
                           'coinbox',
                           () => setNodeMcuOta(deviceId, true),
                           'NodeMCU update armed — it will reflash on its next check-in'
                        )
                     }
                     disabled={busyOrUpdating('coinbox', status.coinBox.state)}
                     blockedReason={blockedReason}
                  />
               )}
               <RowBanner
                  state={status.coinBox.state}
                  what="coinbox"
                  attempts={status.coinBox.attempts}
                  maxAttempts={status.coinBox.maxAttempts}
               />
               <WhatsNew notes={status.coinBox.notes} />
            </Row>
         )}

      </Wrap>
   );
};

/** Right-side control: green "Up to date" tag, a spinner while updating, or
 *  the Update button. */
const RowAction: React.FC<{
   stale: boolean;
   /** Right version, but the config never fully landed — offer Repair, not Update. */
   needsRepair?: boolean;
   state: DeviceUpdateState;
   busy: boolean;
   onUpdate: () => void;
   disabled: boolean;
   /** Set when the router cannot collect an update right now — the button is
    *  greyed and this says why, instead of failing after the click. */
   blockedReason?: string | null;
}> = ({ stale, needsRepair, state, busy, onUpdate, disabled, blockedReason }) => {
   if (state !== 'idle' || busy) {
      return (
         <UpdateBtn disabled>
            <Spinner />
            {state === 'rebooting' ? 'Rebooting…' : 'Updating…'}
         </UpdateBtn>
      );
   }
   // A broken router can be ON the current version — that is exactly how the
   // RouterOS 7.23 half-applied configs hid: "✓ Up to date", no button, while
   // paying customers could not get online. Health, not version, decides.
   if (!stale && !needsRepair) return <DoneTag>✓ Up to date</DoneTag>;
   return (
      <UpdateBtn
         disabled={disabled || !!blockedReason}
         onClick={onUpdate}
         title={blockedReason ?? undefined}
      >
         {needsRepair && !stale ? 'Repair' : 'Update'}
      </UpdateBtn>
   );
};

/** Inline "in progress" banner scoped to the component that's updating. */
const RowBanner: React.FC<{
   state: DeviceUpdateState;
   what: 'router' | 'coinbox';
   /**
    * How many downloads the NodeMCU has already tried, and the ceiling.
    *
    * 🔴 Without these the banner said "armed — it will reflash on its next
    * check-in" and NOTHING ELSE for the whole update, however long it took and
    * however many downloads had already failed. The count only appeared once
    * the box had given up entirely, which is the one moment it stops being
    * useful. Watching 5D ALCARDE work through attempt 2 of 3, the screen looked
    * identical to a box that had not started (Ray, 2026-08-24).
    *
    * An operator standing at a vendo needs to know it is trying, and that
    * trying will end.
    */
   attempts?: number;
   maxAttempts?: number;
}> = ({ state, what, attempts, maxAttempts }) => {
   if (state === 'idle') return null;
   // A first attempt already under way is not the same as one that has already
   // failed and come back: "attempt 2 of 3" tells an operator that time is
   // being used up, which "armed" never did.
   const tries =
      what === 'coinbox' && attempts && attempts > 0
         ? ` Attempt ${attempts}${maxAttempts ? ` of ${maxAttempts}` : ''} — a poor internet line makes this slow, not impossible.`
         : '';
   const msg =
      state === 'rebooting'
         ? 'The router is rebooting to finish the update — it goes offline for a minute or two, then comes back on its own. Don’t unplug it.'
         : what === 'coinbox'
         ? `Update armed — the NodeMCU will reflash on its next check-in. Don’t unplug it.${tries}`
         : 'Update in progress — please wait, don’t unplug the router.';
   return (
      <InlineBanner $rebooting={state === 'rebooting'}>
         {state === 'rebooting' ? '🔄 ' : '⏳ '}
         {msg}
      </InlineBanner>
   );
};

/** "v1.0.12 → v1.0.13" transition chip when behind; single chip when current. */
const shortVer = (v: string | number | null): string =>
   v == null ? '' : `${String(v).split('-')[0]}`;

const VersionTransition: React.FC<{
   from: string | number | null;
   to: string | number | null;
   stale: boolean;
}> = ({ from, to, stale }) => {
   if (!stale) {
      const cur = shortVer(from) || shortVer(to);
      return cur ? <VersionChip $stale={false}>{cur}</VersionChip> : null;
   }
   return (
      <ChipRow>
         <VersionChip $stale={false}>{shortVer(from) || '—'}</VersionChip>
         <Arrow>→</Arrow>
         <VersionChip $stale>{shortVer(to)}</VersionChip>
      </ChipRow>
   );
};

export default UpdatesTab;

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 12px;
`;

const Muted = styled.p`
   font-size: 13px;
   color: ${colors.GRAY5};
   padding: 8px 0;
`;

const Ok = styled.p`
   font-size: 13px;
   font-weight: 600;
   color: ${colors.SUCCESS};
   margin: 0;
`;

// Full-width progress banner under the component row that's updating.
const InlineBanner = styled.div<{ $rebooting: boolean }>`
   flex-basis: 100%;
   width: 100%;
   margin-top: 2px;
   padding: 8px 10px;
   border-radius: 8px;
   font-size: 12px;
   line-height: 1.5;
   color: ${colors.WARNING};
   background: ${colors.WARNING}14;
   border: 1px solid ${colors.WARNING}44;
`;

const Row = styled.div`
   display: flex;
   align-items: flex-start;
   flex-wrap: wrap;
   gap: 12px;
   padding: 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
`;

const RowMain = styled.div`
   flex: 1;
   min-width: 0;
`;

const RowTitle = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   font-size: 14px;
   font-weight: 700;
   margin-bottom: 3px;
`;

// Version pill next to the component name. Neutral when current; orange
// when an update is available so a behind component reads at a glance.
const VersionChip = styled.span<{ $stale: boolean }>`
   padding: 1px 8px;
   border-radius: 999px;
   font-size: 11px;
   font-weight: 700;
   white-space: nowrap;
   color: ${({ $stale }) => ($stale ? colors.WARNING : colors.GRAY4)};
   background: ${({ $stale }) => ($stale ? `${colors.WARNING}1f` : 'rgba(120,130,145,0.1)')};
   border: 1px solid
      ${({ $stale }) => ($stale ? `${colors.WARNING}55` : 'transparent')};
`;

const ChipRow = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 5px;
`;

const Arrow = styled.span`
   color: ${colors.GRAY5};
   font-size: 12px;
   font-weight: 700;
`;

const RowMeta = styled.div`
   font-size: 12px;
   color: ${colors.GRAY4};
   line-height: 1.5;
`;

/**
 * Says plainly whether an update can damage the router.
 *
 * Orange for a firmware update: it rewrites the part of the chip that starts
 * the router, and a power cut mid-write kills it. Green once the router can
 * take the small updates, which only touch the writable area and put the old
 * files back by themselves — proven on hardware by pulling the plug 29 files
 * into a 45-file update.
 *
 * No percentage in either. The only figure we have is 1 in 115 from one
 * rollout, three or four Ruijies have since died, and a number nobody has
 * measured would read as a fact rather than the guess it is.
 */
const RiskBanner = styled.div<{ $safe: boolean }>`
   /* flex-basis 100% so it spans the whole Row rather than sharing width with
      the update button — the reason it read as a cramped aside before. */
   flex: 1 0 100%;
   margin-top: 2px;
   padding: 9px 12px;
   border-radius: 6px;
   font-size: 12.5px;
   line-height: 1.55;
   border: 1px solid ${({ $safe }) => ($safe ? '#86efac' : '#fed7aa')};
   background: ${({ $safe }) => ($safe ? '#f0fdf4' : '#fff7ed')};
   color: ${({ $safe }) => ($safe ? '#166534' : '#9a3412')};
`;
const RiskTitle = styled.div`
   font-weight: 700;
   margin-bottom: 2px;
`;
/*
 * takesSafeUpdates() lived here — "can this router take small updates?", worked
 * out from its running version.
 *
 * Removed 2026-08-25. It existed to show the green banner on any capable
 * router, waiting update or not, and that was the whole misunderstanding: the
 * banner is not a fact about the router, it is the ANSWER TO "what kind is the
 * update sitting in front of me?". With nothing waiting there is no question,
 * so there is no banner. Ray flagged it twice — once for orange, once for
 * green — before it landed.
 *
 * Whether a router is capable is still decided, but server-side and for the
 * thing that matters: whether to OFFER it one (bundleCapable, agent 2.2.0+).
 */

/**
 * Which kind of update is this row about to install?
 *
 * The Update button installs the SAFE one whenever a safe one is waiting, so
 * everything the row says — the wording, the restart warning, the banner — has
 * to agree with this single answer. Keeping it in one place is the point: the
 * first cut worked each of them out separately and got all three wrong in
 * different ways.
 *
 * null means nothing is waiting, and nothing is what the row should then say.
 */
export function pendingUpdateKind(
   fw: { stale?: boolean; scriptUpdate?: { version: string } | null } | null
): 'scripts' | 'firmware' | null {
   if (!fw) return null;
   if (fw.scriptUpdate?.version) return 'scripts';
   return fw.stale ? 'firmware' : null;
}

const Note = styled.div`
   margin-top: 4px;
   font-size: 11.5px;
   color: ${colors.GRAY5};
`;

const ErrLine = styled.div`
   margin-top: 4px;
   color: ${colors.DANGER};
`;

const UpdateBtn = styled.button`
   flex-shrink: 0;
   display: inline-flex;
   align-items: center;
   gap: 7px;
   padding: 8px 16px;
   border: none;
   border-radius: 8px;
   background: ${colors.PRIMARY};
   color: ${colors.WHITE};
   font-size: 13px;
   font-weight: 600;
   cursor: pointer;
   /* A dimmed blue button still reads as "press me" — operators tried, and got
      an error back. Disabled must look like a different object: flat grey, grey
      text, no shadow. */
   &:disabled {
      background: ${colors.GRAY13};
      color: ${colors.GRAY5};
      cursor: not-allowed;
      box-shadow: none;
   }
`;

const spin = keyframes`
   to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
   width: 13px;
   height: 13px;
   flex-shrink: 0;
   border-radius: 50%;
   /* Fixed whites — colors.WHITE is a CSS var, so hex-alpha math on it
      produces invalid CSS and the ring vanishes. */
   border: 2px solid rgba(255, 255, 255, 0.45);
   border-top-color: #ffffff;
   animation: ${spin} 0.7s linear infinite;
`;

const DoneTag = styled.span`
   flex-shrink: 0;
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 5px 11px;
   border-radius: 999px;
   font-size: 12px;
   font-weight: 700;
   color: ${colors.SUCCESS};
   background: ${colors.SUCCESS}18;
   border: 1px solid ${colors.SUCCESS}44;
   white-space: nowrap;
`;

/* Full width, like the risk banners beside it.
   It lived in the left column, which shares the row with the Update button, so
   release notes wrapped into a narrow ribbon with a big empty gap to its right
   — Ray flagged it on a real 2.3.1 canary. `flex: 1 0 100%` breaks it onto its
   own line inside Row; `order` keeps it below the button rather than above. */
const NotesWrap = styled.div`
   flex: 1 0 100%;
   order: 3;
   min-width: 0;
   margin-top: 8px;
`;

const NotesToggle = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 5px;
   padding: 0;
   border: none;
   background: transparent;
   color: ${colors.PRIMARY};
   font-size: 12px;
   font-weight: 600;
   cursor: pointer;
`;

const Caret = styled.span<{ $open: boolean }>`
   display: inline-block;
   font-size: 10px;
   transition: transform 0.15s ease;
   transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
`;

/**
 * 🔴 STACKED, not side by side.
 *
 * First attempt put "View all" inside the What's new panel, so it only existed
 * after opening that one — Ray: "wheres the view all update?". Second attempt
 * put them on one row, and because a version with no note renders no panel at
 * all the two links ended up jammed against each other: "UI too bad".
 *
 * They are two separate questions — "what is in this one" and "what else is
 * there" — so they get two lines, which is also what was asked for: below.
 */
const ToggleRow = styled.div`
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   gap: 6px;
`;

const ViewAllBtn = styled.button`
   background: none;
   border: 0;
   padding: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   &:hover {
      text-decoration: underline;
   }
`;

const HistoryList = styled.div`
   margin-top: 6px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
   /* Bounded. Prod already holds 40 Ruijie versions and an unbounded list
      pushes the Update button off the screen inside a modal. */
   max-height: 300px;
   overflow-y: auto;
`;

const HistoryRow = styled.div`
   padding: 8px 10px;
   & + & {
      border-top: 1px solid ${colors.GRAY12};
   }
`;

const HistoryHead = styled.div`
   display: flex;
   align-items: center;
   gap: 6px;
   flex-wrap: wrap;
`;

const HistoryVer = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.GRAY1};
   font-variant-numeric: tabular-nums;
`;

const HereTag = styled.span`
   font-size: 10px;
   font-weight: 600;
   padding: 1px 6px;
   border-radius: 999px;
   background: ${colors.SUCCESS}1f;
   color: ${colors.SUCCESS};
`;

const OfferTag = styled.span`
   font-size: 10px;
   font-weight: 600;
   padding: 1px 6px;
   border-radius: 999px;
   background: ${colors.PRIMARY}1f;
   color: ${colors.PRIMARY};
`;

const HistoryDate = styled.span`
   margin-left: auto;
   font-size: 10.5px;
   color: ${colors.GRAY5};
`;

const HistoryNotes = styled.div<{ $muted?: boolean }>`
   margin-top: 3px;
   font-size: 11.5px;
   line-height: 1.5;
   white-space: pre-wrap;
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.GRAY3)};
   font-style: ${({ $muted }) => ($muted ? 'italic' : 'normal')};
`;

const NotesBody = styled.div<{ $muted?: boolean }>`
   margin-top: 6px;
   padding: 8px 10px;
   border-radius: 8px;
   background: rgba(120, 130, 145, 0.08);
   border: 1px solid ${colors.GRAY12};
   font-size: 12px;
   line-height: 1.55;
   color: ${({ $muted }) => ($muted ? colors.GRAY5 : colors.GRAY3)};
   white-space: pre-wrap;
   font-style: ${({ $muted }) => ($muted ? 'italic' : 'normal')};
`;

const ManualTag = styled.span`
   flex-shrink: 0;
   padding: 6px 12px;
   border-radius: 8px;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.WARNING};
   background: ${colors.WARNING}18;
`;

// ── Switch product (cloud ⇄ hybrid) ───────────────────────────────────────
// First thing on the tab, so it reads as the headline rather than a footnote.
// Ray, 2026-08-19: "this switch is in very top because this is important to see".
const ServicedBy = styled.div`
   font-size: 11px;
   color: ${colors.GRAY3};
   background: ${colors.GRAY14};
   border-radius: 8px;
   padding: 8px 12px;
   strong {
      color: ${colors.GRAY_DARK};
      font-weight: 700;
   }
`;

const MigrateBox = styled.div`
   margin-bottom: 16px;
   padding: 14px 16px;
   border: 1px solid ${colors.PRIMARY}33;
   border-radius: 12px;
   background: ${colors.PRIMARY}0a;
`;

const SwitchLine = styled.div`
   font-size: 14px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   letter-spacing: 0.1px;
`;

/** What it is now — muted, because it is the part being left behind. */
const Was = styled.span`
   font-weight: 800;
   color: ${colors.GRAY1};
`;

/** What it becomes — carries the emphasis. */
const Will = styled.span`
   font-weight: 800;
   color: ${colors.PRIMARY};
`;

const ConfirmBox = styled.div`
   margin-top: 12px;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid ${colors.ORANGE}55;
   background: ${colors.ORANGE}0d;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.GRAY1};
   strong {
      color: ${colors.MAIN_DARK};
   }
   ul {
      margin: 8px 0 0;
      padding-left: 18px;
   }
   li {
      margin-bottom: 4px;
   }
`;

const ConfirmRow = styled.div`
   display: flex;
   gap: 8px;
   margin-top: 12px;
   flex-wrap: wrap;
`;

const PrimaryBtn = styled.button<{ $danger?: boolean }>`
   border: 0;
   border-radius: 8px;
   padding: 9px 14px;
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.WHITE};
   background: ${({ $danger }) => ($danger ? colors.RED : colors.PRIMARY)};
   cursor: pointer;
   &:disabled {
      background: ${colors.GRAY10};
      color: ${colors.GRAY1};
      cursor: not-allowed;
   }
`;

/** The Update button with "Update later…" under it, right-aligned. */
const ActionStack = styled.div`
   flex-shrink: 0;
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   gap: 6px;
`;

const LaterBtn = styled.button`
   border: 1px solid ${colors.PRIMARY}66;
   border-radius: 8px;
   background: ${colors.WHITE};
   padding: 7px 12px;
   font-size: 12.5px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
   white-space: nowrap;
   &:disabled {
      border-color: ${colors.GRAY12};
      color: ${colors.GRAY5};
      cursor: not-allowed;
   }
`;

const ScheduledWrap = styled.div`
   flex-shrink: 0;
   display: flex;
   flex-direction: column;
   align-items: flex-end;
   gap: 6px;
`;

const ScheduledTag = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   padding: 7px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.PRIMARY}55;
   background: ${colors.PRIMARY}12;
   color: ${colors.MAIN_DARK};
   font-size: 12.5px;
   font-weight: 600;
   white-space: nowrap;
`;

/** Full-width (direct child of Row) picker for "Update later…". */
const ScheduleBox = styled.div`
   flex-basis: 100%;
   padding: 12px 14px;
   border-radius: 10px;
   border: 1px solid ${colors.PRIMARY}55;
   background: ${colors.PRIMARY}0d;
   font-size: 12.5px;
   line-height: 1.5;
   color: ${colors.GRAY1};
   strong {
      color: ${colors.MAIN_DARK};
   }
`;

/** Three equal cards across the whole box — no dead space on the right. */
const PickGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, minmax(0, 1fr));
   gap: 8px;
   margin-top: 10px;
   @media (max-width: 640px) {
      grid-template-columns: 1fr;
   }
`;

const QuickPick = styled.button<{ $on: boolean }>`
   display: flex;
   flex-direction: column;
   align-items: flex-start;
   gap: 2px;
   width: 100%;
   padding: 10px 12px;
   border-radius: 8px;
   border: 1px solid ${({ $on }) => ($on ? colors.PRIMARY : colors.GRAY10)};
   background: ${({ $on }) => ($on ? `${colors.PRIMARY}1a` : colors.WHITE)};
   color: ${colors.MAIN_DARK};
   font-size: 12.5px;
   font-weight: 700;
   text-align: left;
   cursor: pointer;
   small {
      font-weight: 500;
      color: ${colors.GRAY5};
   }
`;

/** Same footprint as a quick pick, holding the free date + time input. */
const PickCard = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
   padding: 8px 12px;
   border-radius: 8px;
   border: 1px solid ${colors.GRAY10};
   background: ${colors.WHITE};
   label {
      font-size: 12.5px;
      font-weight: 700;
      color: ${colors.MAIN_DARK};
   }
   input {
      width: 100%;
      box-sizing: border-box;
      padding: 4px 0;
      border: none;
      border-bottom: 1px solid ${colors.GRAY12};
      font-size: 12.5px;
      color: ${colors.MAIN_DARK};
      background: transparent;
   }
`;

/** Checkbox on the left, buttons on the right, one line. */
const FootRow = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: 12px;
   flex-wrap: wrap;
   margin-top: 12px;
`;

const FootBtns = styled.div`
   display: flex;
   gap: 8px;
   margin-left: auto;
`;

const AutoAllRow = styled.div`
   display: flex;
   align-items: center;
   gap: 8px;
   input {
      width: 16px;
      height: 16px;
      margin: 0;
   }
   label {
      color: ${colors.MAIN_DARK};
      font-weight: 600;
      cursor: pointer;
   }
`;

/** Full-width (direct child of Row) line for the standing rule. */
const AutoLine = styled.div`
   flex-basis: 100%;
   display: flex;
   align-items: center;
   flex-wrap: wrap;
   gap: 6px;
   padding: 8px 12px;
   border-radius: 8px;
   background: ${colors.GRAY13};
   color: ${colors.GRAY1};
   font-size: 12.5px;
   strong {
      color: ${colors.MAIN_DARK};
   }
`;

const AutoOffBtn = styled.button`
   margin-left: auto;
   border: none;
   background: transparent;
   color: ${colors.PRIMARY};
   font-size: 12.5px;
   font-weight: 600;
   text-decoration: underline;
   cursor: pointer;
   &:disabled {
      color: ${colors.GRAY5};
      cursor: not-allowed;
   }
`;


const GhostBtn = styled.button`
   border: 1px solid ${colors.GRAY10};
   border-radius: 8px;
   padding: 9px 14px;
   font-size: 12.5px;
   font-weight: 700;
   color: ${colors.GRAY1};
   background: transparent;
   cursor: pointer;
   &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
   }
`;
